// Supabase client for core package - uses injected client pattern
// The consuming application must inject its client to avoid duplicate GoTrueClient instances
import { createClient, SupabaseClient } from "@supabase/supabase-js";
import { logger } from "../../lib/logger";

// Injected client from the consuming application
let injectedClient: SupabaseClient | null = null;
let lazyClient: SupabaseClient | null = null;

/**
 * Inject a Supabase client from the consuming application.
 * This MUST be called before any services that use the core package.
 * This prevents multiple GoTrueClient instances.
 */
export function injectSupabaseClient(client: SupabaseClient): void {
  injectedClient = client;
}

// Environment variable access with proper browser/Node.js compatibility
const getEnvVar = (key: string): string => {
  // Check if we're in a browser environment
  if (typeof window !== 'undefined') {
    // In browser, check for Vite environment variables
    return (window as any).__ENV__?.[key] || (import.meta as any).env?.[key] || "";
  }

  // In Node.js environment
  if (typeof process !== 'undefined') {
    return process.env?.[key] || "";
  }

  // Fallback
  return "";
};

/**
 * Get the Supabase client.
 * Uses the injected client if available, otherwise creates a lazy singleton.
 * @returns The Supabase client instance
 */
function getClient(): SupabaseClient {
  // Prefer injected client from consuming app
  if (injectedClient) {
    return injectedClient;
  }
  
  // Fallback: create lazy singleton (for backward compatibility)
  if (!lazyClient) {
    const url = getEnvVar('VITE_SUPABASE_URL');
    const key = getEnvVar('VITE_SUPABASE_ANON_KEY');
    
    if (!url || !key) {
      logger.warn('⚠️ Core package: No Supabase client injected and env vars missing. Call injectSupabaseClient() from your app.');
    }
    
    lazyClient = createClient(url || "", key || "");
  }
  
  return lazyClient;
}

// Export a proxy object that lazily gets the client on first use
// This prevents client creation on import
export const supabase: SupabaseClient = new Proxy({} as SupabaseClient, {
  get(_target, prop) {
    const client = getClient();
    const value = (client as any)[prop];
    if (typeof value === 'function') {
      return value.bind(client);
    }
    return value;
  }
});
